import { Checkbox, Grid } from '@dextech-ai/dex-design-system';
import PropTypes from 'prop-types';
import { ROLE_OPTIONS } from '../dsv-role-options';
import { tripleKey } from '../dsv-link-usecase-form';

const PERSONA_FILTER_THRESHOLD = 8;

/**
 * One collapsible section per use case. A persona is "linked" iff ≥1 of its role
 * checkboxes is checked — there is no separate persona-level checkbox and no
 * Remove control (unchecking every role IS the removal staging). Presentational:
 * all state lives in the parent and is passed via props.
 */
function UseCaseAccordion({
	useCaseOptions,
	personaOptions,
	edited,
	expandedUseCaseIds,
	personaFilterByUc,
	linkedCountByUc,
	onToggleExpand,
	onToggleRole,
	onPersonaFilterChange,
}) {
	if (personaOptions.length === 0) {
		return (
			<p className="dsv-promote__hint">
				No personas configured for this environment — seed at least one before linking.
			</p>
		);
	}

	return (
		<Grid columns={1} rowGap={1.2}>
			{useCaseOptions.map((uc) => {
				const open = expandedUseCaseIds.has(uc.id);
				const linkedCount = linkedCountByUc[uc.id] || 0;
				const filter = (personaFilterByUc[uc.id] || '').trim().toLowerCase();
				const showFilter = personaOptions.length > PERSONA_FILTER_THRESHOLD;

				// A persona is "checked" for this use case when ≥1 of its roles is
				// staged. Derived purely from `edited` — no extra state.
				const hasChecked = (p) =>
					ROLE_OPTIONS.some((role) => edited[tripleKey(uc.id, p.id, role.value)]);
				const matchesFilter = (p) =>
					p.name.toLowerCase().includes(filter) ||
					p.id.toLowerCase().includes(filter);

				// When filtering, personas with staged roles stay visible even if they
				// don't match — matches first (original order), then checked-but-
				// non-matching (original order) — so a user never loses sight of a
				// selection the filter would otherwise hide.
				let displayPersonas = personaOptions;
				if (filter) {
					const matching = personaOptions.filter(matchesFilter);
					const checkedNonMatching = personaOptions.filter(
						(p) => !matchesFilter(p) && hasChecked(p),
					);
					displayPersonas = [...matching, ...checkedNonMatching];
				}

				return (
					<section
						className={`dsv-ucp-acc__uc${open ? ' dsv-ucp-acc__uc--open' : ''}`}
						key={uc.id}
						data-testid={`ucp-acc-${uc.id}`}
					>
						<button
							type="button"
							className="dsv-ucp-acc__header"
							aria-expanded={open}
							data-testid={`ucp-acc-header-${uc.id}`}
							onClick={() => onToggleExpand(uc.id)}
						>
							<span className="dsv-ucp-acc__chevron" aria-hidden="true">
								{open ? '▾' : '▸'}
							</span>
							<span className="dsv-ucp-acc__uc-name">{uc.name}</span>
							{linkedCount > 0 && (
								<span className="dsv-ucp-acc__badge">({linkedCount} linked)</span>
							)}
						</button>

						{open && (
							<div className="dsv-ucp-acc__body">
								{showFilter && (
									<div className="dsv-ucp-acc__filter">
										<input
											className="dsv-ucp-acc__search"
											type="text"
											placeholder="Filter personas…"
											data-testid={`ucp-persona-search-${uc.id}`}
											value={personaFilterByUc[uc.id] || ''}
											onChange={(e) => onPersonaFilterChange(uc.id, e.target.value)}
										/>
										{filter && (
											<p className="dsv-ucp-acc__filter-hint">
												Personas with selected roles are always shown.
											</p>
										)}
									</div>
								)}
								<div className="dsv-ucp-acc__persona-list">
									{displayPersonas.map((p) => {
										const checked = hasChecked(p);
										return (
											<div
												className={`dsv-ucp-acc__persona-row${checked ? ' dsv-ucp-acc__persona-row--checked' : ''}`}
												key={p.id}
												data-testid={`ucp-persona-row-${uc.id}-${p.id}`}
											>
												<span className="dsv-ucp-acc__persona-name">
													{p.name} <em>({p.id})</em>
												</span>
												<div className="dsv-ucp-acc__roles">
													{ROLE_OPTIONS.map((role) => (
														<Checkbox
															key={role.value}
															data-testid={`ucp-role-${uc.id}-${p.id}-${role.value}`}
															checked={Boolean(edited[tripleKey(uc.id, p.id, role.value)])}
															onCheckedChange={() => onToggleRole(uc.id, p.id, role.value)}
														>
															{role.label}
														</Checkbox>
													))}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}
					</section>
				);
			})}
		</Grid>
	);
}

UseCaseAccordion.propTypes = {
	useCaseOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
	personaOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
	edited: PropTypes.object.isRequired,
	expandedUseCaseIds: PropTypes.instanceOf(Set).isRequired,
	personaFilterByUc: PropTypes.object.isRequired,
	linkedCountByUc: PropTypes.object.isRequired,
	onToggleExpand: PropTypes.func.isRequired,
	onToggleRole: PropTypes.func.isRequired,
	onPersonaFilterChange: PropTypes.func.isRequired,
};

export default UseCaseAccordion;
